import React from 'react';
import './GameCard.css';

interface Game {
  id: number;
  week: number;
  season: number;
  home_team: string;
  away_team: string;
  home_team_abbr?: string;
  away_team_abbr?: string;
  home_team_logo?: string;
  away_team_logo?: string;
  game_date: string;
  home_spread: number;
  home_score?: number;
  away_score?: number;
  is_final: boolean;
  locked?: boolean;
}

interface Pick {
  pick_value: string;
}

interface GameCardProps {
  game: Game;
  userPick?: Pick;
  pendingPick?: string;
  onMakePick: (gameId: number, pickValue: string) => void;
}

const GameCard: React.FC<GameCardProps> = ({ game, userPick, pendingPick, onMakePick }) => {
  const nowEastern = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const gameTime = new Date(game.game_date);
  const gameStarted = gameTime <= nowEastern;
  const timeUntilGame = gameTime.getTime() - nowEastern.getTime();
  const minutesUntilGame = Math.floor(timeUntilGame / (1000 * 60));
  const hoursUntilGame = Math.floor(minutesUntilGame / 60);
  
  // Determine which pick to show (pending takes precedence over saved)
  const currentPick = pendingPick || userPick?.pick_value;

  // Enhanced game status logic
  const getGameStatus = () => {
    if (game.is_final) return { status: 'Final', class: 'final' };
    if (game.locked) return { status: '🔒 Locked', class: 'locked' };
    if (gameStarted) return { status: 'Game Started', class: 'started' };
    if (minutesUntilGame <= 15) return { status: `⚠️ Locks in ${minutesUntilGame}m`, class: 'critical' };
    if (minutesUntilGame <= 60) return { status: `Locks in ${minutesUntilGame}m`, class: 'warning' };
    if (hoursUntilGame <= 3) return { status: `Locks in ${hoursUntilGame}h ${minutesUntilGame % 60}m`, class: 'closing' };
    return { status: 'Picks Open', class: 'open' };
  };

  const gameStatusInfo = getGameStatus();

  // Determine if picks are locked
  const isLocked = game.locked || gameStarted || game.is_final;

  const getPickResult = (): 'correct' | 'incorrect' | 'pending' => {
    if (!game.is_final || !currentPick || game.home_score === undefined || game.away_score === undefined) {
      return 'pending';
    }

    // Apply spread to home team score
    const homeScoreWithSpread = game.home_score + game.home_spread;
    
    if (currentPick === 'home') {
      // Home pick wins if home team + spread > away team
      return homeScoreWithSpread > game.away_score ? 'correct' : 'incorrect';
    } else if (currentPick === 'away') {
      // Away pick wins if away team > home team + spread
      return game.away_score > homeScoreWithSpread ? 'correct' : 'incorrect';
    }
    
    return 'pending';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York'
    });
  };

  const getSpreadDisplay = (spread: number) => {
    return spread > 0 ? `+${spread}` : spread.toString();
  };

  const handlePickSelection = (pickValue: string) => {
    if (isLocked) return;
    onMakePick(game.id, pickValue);
  };

  const getPickStatusClass = () => {
    if (!game.is_final || !currentPick) return '';
    const result = getPickResult();
    if (result === 'correct') return 'pick-correct';
    if (result === 'incorrect') return 'pick-incorrect';
    return '';
  };

  return (
    <div className={`game-card ${getPickStatusClass()} ${isLocked ? 'locked' : ''} ${currentPick ? 'has-pick' : ''}`}>
      <div className="game-header">
        <div className="teams">
          <div className="away-team" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {game.away_team_logo && (
              <img 
                src={game.away_team_logo} 
                alt={`${game.away_team_abbr || 'Away team'} logo`}
                style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <div>
              <span className="team-name">{game.away_team}</span>
              {(gameStarted || game.is_final) && game.away_score !== undefined && (
                <div className="score">{game.away_score}</div>
              )}
            </div>
          </div>
          <div className="vs">@</div>
          <div className="home-team" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {game.home_team_logo && (
              <img 
                src={game.home_team_logo} 
                alt={`${game.home_team_abbr || 'Home team'} logo`}
                style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <div>
              <span className="team-name">{game.home_team}</span>
              {(gameStarted || game.is_final) && game.home_score !== undefined && (
                <div className="score">{game.home_score}</div>
              )}
            </div>
          </div>
        </div>
        <div className="game-time-status">
          <div className="game-time">{formatDate(game.game_date)}</div>
          <div className={`game-status-badge ${gameStatusInfo.class}`}>
            {gameStatusInfo.status}
          </div>
        </div>
      </div>

      {/* Spread Betting Section */}
      <div className="betting-section">
        <h4>Point Spread {isLocked && '🔒'}</h4>
        {isLocked && !currentPick && (
          <div className="locked-message">
            <span>⚠️ {game.locked ? 'Game locked' : 'Game started'} - no pick made</span>
          </div>
        )}
        {gameStatusInfo.class === 'critical' && !isLocked && (
          <div className="urgent-warning">
            <span>🚨 Picks lock in {minutesUntilGame} minutes!</span>
          </div>
        )}
        <div className="betting-options">
          <button
            className={`bet-option ${
              currentPick === 'away' ? 'selected' : ''
            } ${pendingPick === 'away' ? 'pending' : ''} ${isLocked ? 'disabled locked' : ''}`}
            onClick={() => handlePickSelection('away')}
            disabled={isLocked}
            title={isLocked ? (game.locked ? 'Game is locked' : 'Game has started - picks are locked') : ''}
          >
            {game.away_team} {getSpreadDisplay(-game.home_spread)}
            {isLocked && currentPick === 'away' && ' ✓'}
          </button>
          <button
            className={`bet-option ${
              currentPick === 'home' ? 'selected' : ''
            } ${pendingPick === 'home' ? 'pending' : ''} ${isLocked ? 'disabled locked' : ''}`}
            onClick={() => handlePickSelection('home')}
            disabled={isLocked}
            title={isLocked ? (game.locked ? 'Game is locked' : 'Game has started - picks are locked') : ''}
          >
            {game.home_team} {getSpreadDisplay(game.home_spread)}
            {isLocked && currentPick === 'home' && ' ✓'}
          </button>
        </div>
      </div>

      {/* Show current pick status */}
      {currentPick && (
        <div className={`existing-pick ${pendingPick ? 'pending-pick' : 'saved-pick'} ${isLocked ? 'locked-pick' : ''}`}>
          <strong>
            {isLocked ? 'Final Pick:' : pendingPick ? 'Pending Pick:' : 'Saved Pick:'}
          </strong> {currentPick === 'home' ? game.home_team : game.away_team}
          {isLocked && currentPick && ' 🔒'}
          {game.is_final && currentPick && (
            <span className={`pick-result ${getPickResult()}`}>
              {getPickResult() === 'correct' ? ' ✅ WIN' : ' ❌ LOSE'}
            </span>
          )}
        </div>
      )}

      {/* Countdown timer for games closing soon */}
      {gameStatusInfo.class === 'critical' && !isLocked && (
        <div className="countdown-timer">
          <div className="timer-bar">
            <div 
              className="timer-fill" 
              style={{ 
                width: `${Math.max(0, (15 - minutesUntilGame) / 15 * 100)}%`,
                backgroundColor: minutesUntilGame <= 5 ? '#ff4757' : '#ffa502'
              }}
            />
          </div>
          <span className="timer-text">
            {minutesUntilGame}m {Math.floor((60 - (nowEastern.getSeconds())))%60}s until lock
          </span>
        </div>
      )}

      {/* Game status - removed redundant status since we have the badge */}
    </div>
  );
};

export default GameCard;